import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Empty, message, Pagination, Table, Tooltip, Tag } from 'antd';
import { MakeApiCall } from '../../../../../apis';
import { AddCommas, ConvertParams, defaultPageSize } from '../../../../../config';
import { fakeProImage } from '../../../../../config/fakeData';
import ProductDetailsModal from "../../../../../components/modal";
import { getAmount } from "../../../../../utils/helper";
import AddCogs from "../../../products/lib/addCogs";
import SalesRankModal from './SalesRankModal';

const AMAZON_BASE_URL = "https://www.amazon.in/dp/";

/** Single-line headers; ref forwarded for rc-table measure/sticky behavior */
const ProductTableHeaderCell = React.forwardRef((props, ref) => {
    const { style, ...rest } = props;
    return <th {...rest} ref={ref} style={{ ...style, whiteSpace: 'nowrap' }} />;
});
ProductTableHeaderCell.displayName = 'ProductTableHeaderCell';

/** Match Ant Design thead (fw-bolder ~13–14px) for width ≈ rendered header */
const PL_HEADER_MEASURE_FONT = '600 13px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

let _plHeaderMeasureCanvas;
function plMeasureHeaderTextPx(text) {
    const s = String(text ?? '');
    if (typeof document === 'undefined') {
        return Math.ceil(s.length * 7.5);
    }
    if (!_plHeaderMeasureCanvas) _plHeaderMeasureCanvas = document.createElement('canvas');
    const ctx = _plHeaderMeasureCanvas.getContext('2d');
    ctx.font = PL_HEADER_MEASURE_FONT;
    return Math.ceil(ctx.measureText(s).width);
}

/**
 * Column width from header only (label + optional info icon + optional sorter), not cell content.
 */
function plHeaderColumnWidth(label, { hasInfoIcon = false, hasSorter = false } = {}) {
    const textPx = plMeasureHeaderTextPx(label);
    const iconPx = hasInfoIcon ? 28 : 0;
    const sortPx = hasSorter ? 28 : 0;
    const thPaddingPx = 32;
    return Math.max(72, textPx + iconPx + sortPx + thPaddingPx);
}

// ─── Pastel color palette for tags (18 distinct colors) ───────────────────────
const PASTEL_COLORS = [
    { bg: '#e3f2fd', border: '#bbdefb', text: '#1565c0', accent: '#64b5f6' }, // sky
    { bg: '#fce4ec', border: '#f8bbd0', text: '#c62828', accent: '#e57373' }, // rose
    { bg: '#e8f5e9', border: '#c8e6c9', text: '#2e7d32', accent: '#81c784' }, // mint
    { bg: '#fff3e0', border: '#ffe0b2', text: '#e65100', accent: '#ffb74d' }, // peach
    { bg: '#f3e5f5', border: '#e1bee7', text: '#7b1fa2', accent: '#ba68c8' }, // lavender
    { bg: '#e0f7fa', border: '#b2ebf2', text: '#00838f', accent: '#4dd0e1' }, // cyan
    { bg: '#fff8e1', border: '#ffecb3', text: '#f57f17', accent: '#ffd54f' }, // butter
    { bg: '#e8eaf6', border: '#c5cae9', text: '#283593', accent: '#7986cb' }, // indigo
    { bg: '#e0f2f1', border: '#b2dfdb', text: '#00695c', accent: '#4db6ac' }, // teal
    { bg: '#fbe9e7', border: '#ffccbc', text: '#bf360c', accent: '#ff8a65' }, // coral
    { bg: '#f1f8e9', border: '#dcedc8', text: '#558b2f', accent: '#aed581' }, // lime
    { bg: '#ede7f6', border: '#d1c4e9', text: '#4527a0', accent: '#9575cd' }, // grape
    { bg: '#efebe9', border: '#d7ccc8', text: '#4e342e', accent: '#a1887f' }, // mocha
    { bg: '#e1f5fe', border: '#b3e5fc', text: '#0277bd', accent: '#4fc3f7' }, // azure
    { bg: '#fce4ec', border: '#f48fb1', text: '#ad1457', accent: '#f06292' }, // pink
    { bg: '#f9fbe7', border: '#e6ee9c', text: '#827717', accent: '#c0ca33' }, // olive
    { bg: '#e0ecff', border: '#b3d1ff', text: '#1a4d8f', accent: '#5b9bd5' }, // steel
    { bg: '#fff0f5', border: '#ffc1d6', text: '#9c1a47', accent: '#e0738a' }, // blush
];

// Sequential color assignment — each unique tag gets the next unused color
const _tagColorMap = new Map();
let _tagColorIndex = 0;

const getTagColor = (tagName) => {
    const key = (tagName || '').toLowerCase().trim();
    if (_tagColorMap.has(key)) return _tagColorMap.get(key);
    const color = PASTEL_COLORS[_tagColorIndex % PASTEL_COLORS.length];
    _tagColorMap.set(key, color);
    _tagColorIndex++;
    return color;
};

export const FILTERABLE_COLUMNS = [
    { key: 'productSearch',    label: 'Product (SKU / ASIN / Title)', type: 'text'     },
    { key: 'listing_status',   label: 'Listing Status',               type: 'dropdown' },
    { key: 'fulfillment_type', label: 'Fulfillment Type',             type: 'dropdown' },
    { key: 'tags',             label: 'Tags',                         type: 'dropdown' },
    { key: 'price',            label: 'Price',                        type: 'numeric'  },
    { key: 'total_units',      label: 'Units Sold',                   type: 'numeric'  },
    { key: 'refund_unit',      label: 'Refund Units',                 type: 'numeric' },
    { key: 'refund_cost',      label: 'Refund Cost',                  type: 'numeric' },
    { key: 'sales',            label: 'Sales',                        type: 'numeric' },
    { key: 'ad_sales',         label: 'Ad Sales',                     type: 'numeric' },
    { key: 'ad_spend',         label: 'Ad Spend',                     type: 'numeric' },
    { key: 'cogs',             label: 'COGS',                         type: 'numeric' },
    { key: 'organic_sales',    label: 'Organic Sales',                type: 'numeric' },
    { key: 'tax',              label: 'Tax Collected',                type: 'numeric' },
    { key: 'fees',             label: 'Fees',                         type: 'numeric' },
    { key: 'gross_profit',     label: 'Gross Profit',                 type: 'numeric' },
    { key: 'net_profit',       label: 'Net Profit',                   type: 'numeric' },
    { key: 'inventory',        label: 'FBA Inventory',                    type: 'numeric' },
    { key: 'fbm_inventory',    label: 'FBM Inventory',                type: 'numeric' },
    { key: 'days_on_hand',     label: 'Days Until Stockout',          type: 'numeric' },
    { key: 'inventory_status', label: 'FBA Inventory Health',             type: 'dropdown' },
    { key: 'cpc',                 label: 'CPC',                type: 'numeric' },
    { key: 'acos',                label: 'ACoS',               type: 'numeric' },
    { key: 'roas',                label: 'ROAS',               type: 'numeric' },
    { key: 'troas',               label: 'TRoAS',              type: 'numeric' },
    { key: 'tacos',               label: 'TACoS',              type: 'numeric' },
    { key: 'sales_rank',                 label: 'BSR',                type: 'numeric' },
    { key: 'ad_attribution',      label: 'Ad Attribution',     type: 'numeric' },
    { key: 'profit_percent',      label: 'Profit %',           type: 'numeric' },
    { key: 'sales_contribution',  label: 'Sales Contribution', type: 'numeric' },
];

/** Header tooltips for product P&L table (matches Expenses table info-icon pattern). */
const PRODUCT_COL_HEADER_HELP = {
    tags: 'Custom labels you assign to products for filtering and grouping SKU level P&L.',
    total_units: 'Total number of individual product units sold in the selected period, including refunded units.',
    refund_unit: 'Number of units returned by customers and refunded during the selected period.',
    refund_cost: 'Total monetary value lost to refunds — includes product cost and any restocking or return fees.',
    sales: 'Total gross revenue from all orders before deducting refunds, fees, or ad spend.',
    ad_sales: 'Revenue attributed directly to sponsored/paid advertising campaigns.',
    ad_spend: 'Total amount spent on advertising campaigns in the period.',
    cogs: 'Cost Of Goods Sold — what you paid to source or manufacture the units sold. Adjust COGS from products tab.',
    organic_sales: 'Revenue from non-paid traffic — customers who found your product without clicking an ad.',
    tax: 'Sales tax collected from customers on eligible orders. Typically remitted to the government.',
    fees: 'All Amazon charges including FBA fulfillment fees, referral fees, storage fees, and closing fees etc.',
    gross_profit: 'Total Sales - Amazon Fees - Advertising Costs - Cost of Goods',
    net_profit: 'What you actually keep: Gross profit - Expense - Tax. The true bottom line.',
    inventory: 'Current number of units stored in Amazon\'s fulfillment centers, available to ship to customers.',
    fbm_inventory: 'Units you fulfill yourself (Fulfilled by Merchant) stored at your own warehouse, not at Amazon\'s centers.',
    days_on_hand: 'Estimated number of days your current inventory will last based on your recent sales velocity. Helps prevent running out of stock.',
    inventory_status: 'A measure of how healthy your stock levels are based on days left in inventory versus the replenishment lead time you\'ve set in the Products tab. At Risk means you need to book FBA shipments now.',
    cpc: 'Cost Per Click — the average amount you pay each time a shopper clicks on your sponsored ad.',
    acos: 'Advertising Cost of Sales = (Ad Spend / Ad Sales) × 100. Measures ad efficiency; lower is generally better.',
    roas: 'Return on Ad Spend = Ad Sales / Ad Spend. The inverse of ACoS; shows revenue generated per rupee/dollar spent on ads.',
    troas: 'Total Return on Ad Spend = Ad Sales ÷ Total Sales. Unlike RoAS which measures return purely against ad sales, TRoAS shows how your ad revenue stacks up against your overall (ads + organic) sales.',
    tacos: 'Total Advertising Cost of Sales = Ad Spend ÷ Total Sales × 100. Unlike ACoS, this includes organic sales in the denominator, giving a truer picture of ad impact on overall business.',
    sales_rank: 'Best Seller Rank — Amazon\'s ranking of your product within its category based on recent sales. Lower number = better rank. Updates hourly.',
    ad_attribution: 'The percentage of your total sales that is coming from advertising. Shows how reliant your overall revenue is on paid ads.',
    profit_percent: 'Net Profit as a percentage of Sales. Shows how much of every rupee/dollar in revenue you actually keep after all costs.',
    sales_contribution: 'The percentage of total sales that a specific product, tag, or segment contributes to your overall revenue.',
};

const productColTitle = (label, columnKey) => {
    const tip = PRODUCT_COL_HEADER_HELP[columnKey];
    if (!tip) return label;
    return (
        <div className="d-flex align-items-center" style={{ whiteSpace: 'nowrap' }}>
            {label}
            <Tooltip title={tip} placement="top">
                <InfoCircleOutlined className="ms-2 text-gray-500" style={{ fontSize: '12px', cursor: 'help', flexShrink: 0 }} />
            </Tooltip>
        </div>
    );
};

// ─── Pure helpers ─────────────────────────────────────────────────────────────

const getFilterableValue = (row, key) => {
    switch (key) {
        case 'productSearch':
            return `${row.sku ?? ''} ${row.asin ?? ''} ${row.product_title ?? ''}`;
        case 'tags':
            return Array.isArray(row.tags) ? row.tags.join(' ') : '';
        case 'listing_status':
            return row.listing_status ?? '';
        case 'fulfillment_type':
            // The product row stores this as fulfillment_channel
            return row.fulfillment_type ?? row.fulfillment_channel ?? '';
        case 'tax':
            return row?.more_data?.tax_liability?.total ?? 0;
        case 'status':
            return row.status === 0 ? 'Active' : 'Inactive';
        case 'troas':
            return row?.more_data?.TRoAS ?? 0;
        case 'tacos':
            return row?.more_data?.TACoS ?? 0;
        case 'sales_contribution':
            return row?.more_data?.asin_sale_contribution ?? 0;
        default:
            // Check top-level first, then fallback into more_data for preset attributes
            if (row[key] !== undefined && row[key] !== null) return row[key];
            if (row?.more_data?.[key] !== undefined && row?.more_data?.[key] !== null) return row.more_data[key];
            return '';
    }
};

const applyFilters = (data, activeFilters) => {
    if (!activeFilters.length) return data;
    return data.filter(row =>
        activeFilters.every(f => {
            // ── Dropdown type: always uses exact-equality ─────────────────
            if (f.type === 'dropdown') {
                if (f.column === 'tags') {
                    // Tags is an array — check if the selected value is present
                    const tagsArr = Array.isArray(row.tags) ? row.tags : [];
                    return tagsArr.some(t => String(t).toLowerCase() === String(f.value).toLowerCase());
                }
                const cellValue = getFilterableValue(row, f.column);
                return String(cellValue).toLowerCase() === String(f.value).toLowerCase();
            }
            const cellValue = getFilterableValue(row, f.column);
            if (f.type === 'numeric') {
                const n = parseFloat(cellValue);
                const threshold = parseFloat(f.value);
                if (isNaN(threshold)) return true;
                if (f.operator === 'gt')  return n > threshold;
                if (f.operator === 'lt')  return n < threshold;
                if (f.operator === 'gte') return n >= threshold;
                if (f.operator === 'lte') return n <= threshold;
                if (f.operator === 'eq')  return n === threshold;
            } else {
                const s = String(cellValue).toLowerCase();
                const q = String(f.value).toLowerCase();
                if (f.operator === 'contains') return s.includes(q);
                if (f.operator === 'eq')       return s === q;
            }
            return true;
        })
    );
};

// ─── Sort comparators (static — defined outside component to stay stable) ─────
const COLUMN_SORTERS = {
    total_units:      (a, b) => (a.total_units      ?? 0) - (b.total_units      ?? 0),
    refund_unit:      (a, b) => (a.refund_unit       ?? 0) - (b.refund_unit       ?? 0),
    refund_cost:      (a, b) => (a.refund_cost       ?? 0) - (b.refund_cost       ?? 0),
    sales:            (a, b) => (a.sales             ?? 0) - (b.sales             ?? 0),
    ad_sales:         (a, b) => (a.ad_sales          ?? 0) - (b.ad_sales          ?? 0),
    ad_spend:         (a, b) => (a.ad_spend          ?? 0) - (b.ad_spend          ?? 0),
    cogs:             (a, b) => (a.cogs              ?? 0) - (b.cogs              ?? 0),
    organic_sales:    (a, b) => (a.organic_sales     ?? 0) - (b.organic_sales     ?? 0),
    tax:              (a, b) => (a?.more_data?.tax_liability?.total ?? 0) - (b?.more_data?.tax_liability?.total ?? 0),
    fees:             (a, b) => (a.fees              ?? 0) - (b.fees              ?? 0),
    gross_profit:     (a, b) => (a.gross_profit      ?? 0) - (b.gross_profit      ?? 0),
    net_profit:       (a, b) => (a.net_profit        ?? 0) - (b.net_profit        ?? 0),
    inventory:        (a, b) => (a.inventory         ?? 0) - (b.inventory         ?? 0),
    fbm_inventory:    (a, b) => (a.fbm_inventory     ?? 0) - (b.fbm_inventory     ?? 0),
    days_on_hand:     (a, b) => (a.days_on_hand      ?? 0) - (b.days_on_hand      ?? 0),
    inventory_status: (a, b) => (a.inventory_status || '').localeCompare(b.inventory_status || ''),
    cpc:                  (a, b) => (a?.more_data?.cpc ?? 0) - (b?.more_data?.cpc ?? 0),
    acos:                 (a, b) => (a?.more_data?.acos ?? 0) - (b?.more_data?.acos ?? 0),
    roas:                 (a, b) => (a.roas ?? 0) - (b.roas ?? 0),
    troas:                (a, b) => (a?.more_data?.TRoAS ?? 0) - (b?.more_data?.TRoAS ?? 0),
    tacos:                (a, b) => (a?.more_data?.TACoS ?? 0) - (b?.more_data?.TACoS ?? 0),
    sales_rank:                  (a, b) => (a?.sales_rank ?? 0) - (b?.sales_rank ?? 0),
    ad_attribution:       (a, b) => (a?.more_data?.ad_attribution ?? 0) - (b?.more_data?.ad_attribution ?? 0),
    profit_percent:       (a, b) => (a?.more_data?.profit_percent ?? 0) - (b?.more_data?.profit_percent ?? 0),
    sales_contribution:   (a, b) => (a?.more_data?.asin_sale_contribution ?? 0) - (b?.more_data?.asin_sale_contribution ?? 0),
};

// ─── Products component ───────────────────────────────────────────────────────

const Products = ({
                      userData,
                      selectedMarketplace,
                      selectedCurrencySign,
                      selectedTileData,
                      groupBy,
                      onColumnsReady,
                      onFiltersReady,
                      onFilteredAsinsChange,
                  }) => {

    const [tableLoading, setTableLoading]         = useState(false);
    const [totalsLoading, setTotalsLoading]       = useState(false);
    const [allProducts, setAllProducts]           = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [totals, setTotals]                     = useState(null);
    const [totalCount, setTotalCount]             = useState(0);
    const [isModalOpen, setIsModalOpen]           = useState(false);
    const [selectedRow, setSelectedRow]           = useState(null);
    const [isCogsModalOpen, setIsCogsModalOpen]   = useState(false);
    const [selectedCogsRecord, setSelectedCogsRecord] = useState(null);
    const [isMobile, setIsMobile]                 = useState(window.innerWidth < 768);
    const [activeFilters, setActiveFilters]       = useState([]);
    const [sortState, setSortState]               = useState({ columnKey: null, order: null });
    const [page, setPage]                         = useState(1);
    const [pageSize, setPageSize]                 = useState(defaultPageSize);
    const [expandedRowKeys, setExpandedRowKeys]   = useState([]);
    const [isHeaderSticky, setIsHeaderSticky]     = useState(false);
    const [filterDropdownOptions, setFilterDropdownOptions] = useState({});

    // bsr modal state
    const [isBsrModalOpen, setIsBsrModalOpen]   = useState(false);
    const [selectedBsrRecord, setSelectedBsrRecord] = useState(null);

    const [apiFilter, setApiFilter] = useState({
        marketplace_id: selectedMarketplace?.id,
        start_date:     selectedTileData?.start_date,
        end_date:       selectedTileData?.end_date,
        sku:            selectedTileData?.sku,
        ...(groupBy ? { group_by: groupBy } : {}),
    });

    const isFirstRender    = useRef(true);
    const inflight         = useRef(null);
    const hadFiltersRef    = useRef(false);
    const lastTotalsKeyRef = useRef(null);
    const tableContainerRef = useRef(null);

    // ─── Stable refs for values used inside callbacks ─────────────────────
    // Storing these in refs prevents fetchTotals/fetchProducts from being
    // recreated every time userData changes, which was causing the useEffect
    // to re-fire and issue duplicate API calls on page reload.
    const tokenRef    = useRef(userData?.token);
    useEffect(() => { tokenRef.current = userData?.token; }, [userData?.token]);

    const handleResize = useCallback(() => setIsMobile(window.innerWidth < 768), []);
    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);

    const openDetails = useCallback(record => {
        setSelectedRow(record);
        setIsModalOpen(true);
    }, []);

    const handleEditCogs = useCallback((e, record) => {
        e.stopPropagation();
        setSelectedCogsRecord(record);
        setIsCogsModalOpen(true);
    }, []);

    // ─── Filter mutation handlers ─────────────────────────────────────────────
    const addFilter = useCallback(filterObj => {
        setActiveFilters(prev => [...prev, filterObj]);
        setPage(1);
    }, []);

    const removeFilter = useCallback(id => {
        setActiveFilters(prev => prev.filter(f => f.id !== id));
        setPage(1);
    }, []);

    const updateFilter = useCallback((id, updatedFields) => {
        setActiveFilters(prev =>
            prev.map(f => f.id === id ? { ...f, ...updatedFields } : f)
        );
        setPage(1);
    }, []);

    const clearFilters = useCallback(() => {
        setActiveFilters([]);
        setPage(1);
    }, []);

    const handleTableChange = useCallback((_, __, sorter) => {
        setSortState({
            columnKey: sorter.order ? sorter.columnKey : null,
            order:     sorter.order || null,
        });
        setPage(1);
    }, []);

    // ─── Isolated totals fetcher (stable — uses refs, never recreated) ──────
    const fetchTotals = useCallback(async (filter, skuParam) => {
        if (!filter?.marketplace_id) return;

        const token = tokenRef.current;
        setTotalsLoading(true);
        try {
            const params = {
                ...filter,
                ...(skuParam ? { sku: skuParam } : {}),
            };
            const res = await MakeApiCall(
                `dashboard-optimized/tiles/get-product-analytics-data-total${ConvertParams(params)}`,
                'get', null, true, {}, token
            );
            if (res?.status) {
                setTotals(res.data);
            }
        } catch {
            // non-critical — keep the last known totals visible
        } finally {
            setTotalsLoading(false);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const buildTotalsKey = useCallback((filter, skuParam) => JSON.stringify({
        marketplace_id: filter?.marketplace_id || null,
        start_date: filter?.start_date || null,
        end_date: filter?.end_date || null,
        group_by: filter?.group_by || null,
        sku: skuParam || '',
    }), []);

    const maybeFetchTotals = useCallback((filter, skuParam) => {
        if (!filter?.marketplace_id) return;
        const nextKey = buildTotalsKey(filter, skuParam);
        if (lastTotalsKeyRef.current === nextKey) return;
        lastTotalsKeyRef.current = nextKey;
        fetchTotals(filter, skuParam);
    }, [buildTotalsKey, fetchTotals]);

    // ─── Re-apply filters, notify parent, refresh totals ─────────────────────
    useEffect(() => {
        const skuList = apiFilter?.sku
            ? apiFilter.sku.split(',').map(s => s.trim()).filter(Boolean)
            : [];

        const skuFiltered = skuList.length > 0
            ? allProducts.filter(p => skuList.includes(p.sku))
            : allProducts;

        const result = applyFilters(skuFiltered, activeFilters);
        setFilteredProducts(result);

        if (typeof onFilteredAsinsChange === 'function') {
            onFilteredAsinsChange(
                activeFilters.length === 0
                    ? null
                    : result.map(p => p.sku).filter(Boolean)
            );
        }

        const hasSkuFilter    = skuList.length > 0;
        const hasColumnFilter = activeFilters.length > 0;

        if (allProducts.length === 0 || !apiFilter?.marketplace_id) return;

        if (hasSkuFilter || hasColumnFilter) {
            hadFiltersRef.current = true;
            const skuParam = result.map(p => p.sku).filter(Boolean).join(',');
            maybeFetchTotals(apiFilter, skuParam || undefined);
        } else if (hadFiltersRef.current) {
            // All filters were just removed — refresh totals for the full dataset
            hadFiltersRef.current = false;
            maybeFetchTotals(apiFilter);
        }

    }, [activeFilters, allProducts, apiFilter?.sku, apiFilter?.marketplace_id, apiFilter?.start_date, apiFilter?.end_date, apiFilter?.group_by, maybeFetchTotals]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Surface filter controls to parent (ItemMaster) ───────────────────────
    useEffect(() => {
        if (typeof onFiltersReady !== 'function') return;
        onFiltersReady({
            filterableColumns: FILTERABLE_COLUMNS,
            activeFilters,
            addFilter,
            updateFilter,
            removeFilter,
            clearFilters,
            filterDropdownOptions,
        });
    }, [activeFilters, addFilter, updateFilter, removeFilter, clearFilters, onFiltersReady, filterDropdownOptions]);

    // ─── Data fetching (server-side pagination, stable callback via refs) ────
    const fetchProducts = useCallback(async (currentFilter, currentPage, currentPageSize) => {
        if (!currentFilter?.marketplace_id) {
            setAllProducts([]);
            setTotalCount(0);
            return;
        }

        const token  = tokenRef.current;
        const { sku, group_by, ...restFilter } = currentFilter;
        const requestKey = JSON.stringify({ ...currentFilter, page: currentPage, pageSize: currentPageSize });
        if (inflight.current === requestKey) return;
        inflight.current = requestKey;

        setTableLoading(true);

        try {
            const fetchParams = {
                ...restFilter,
                ...(group_by ? { group_by } : {}),
                page: currentPage,
                'per-page': currentPageSize,
            };
            const dataRes = await MakeApiCall(
                `dashboard-optimized/tiles/get-product-analytics-data${ConvertParams(fetchParams)}`,
                'get', null, true, {}, token
            );

            if (dataRes?.status) {
                const raw = dataRes.data?.records;

                // Extract filter dropdown options if provided by the API
                if (dataRes.data?.filter_dropdown_options) {
                    const opts = dataRes.data.filter_dropdown_options;
                    setFilterDropdownOptions(opts);
                }
                const pagination = dataRes.data?.pagination;
                if (pagination?.totalCount !== undefined) {
                    setTotalCount(pagination.totalCount);
                }

                let records;

                if (group_by) {
                    // Grouped mode: each item is { group_key, group_type, aggregate, children }
                    // Transform into a tree structure for Ant Design's children-based tree table.
                    const summaryData = Array.isArray(raw?.summaryData) ? raw.summaryData : [];
                    records = summaryData.map((item, pIdx) => {
                        const childRows = (item.children || []).map((child, cIdx) => ({
                            ...child,
                            _rowKey:        child.sku || child.asin || `C__${item.group_key}_${cIdx}`,
                            _isGroupChild:  true,
                        }));

                        const parentRow = {
                            ...item.aggregate,
                            _rowKey:        `P__${item.group_key || pIdx}`,
                            _isGroupParent: true,
                            _groupKey:      item.group_key,
                            _groupType:     group_by,
                        };

                        // Only attach `children` when there are 2+ variants.
                        if (childRows.length > 1) {
                            parentRow.children = childRows;
                        }

                        return parentRow;
                    });
                } else {
                    records = Array.isArray(raw)
                        ? raw
                        : Array.isArray(raw?.summaryData)
                            ? raw.summaryData
                            : [];
                }

                setAllProducts(records);
            } else {
                message.warning(dataRes?.message || 'Failed to load product data');
            }
        } catch {
            message.error('Failed to load product data');
        } finally {
            setTableLoading(false);
            inflight.current = null;
        }
    }, [fetchTotals]); // eslint-disable-line react-hooks/exhaustive-deps

    // After a COGS save: force a fresh fetch.
    const refreshProducts = useCallback(() => {
        inflight.current = null; // allow a fresh request
        lastTotalsKeyRef.current = null; // ensure totals refresh after mutation
        maybeFetchTotals(apiFilter);
        fetchProducts(apiFilter, page, pageSize);
    }, [apiFilter, page, pageSize, fetchProducts, maybeFetchTotals]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetchProducts(apiFilter, page, pageSize);
    }, [apiFilter, page, pageSize, fetchProducts]);

    useEffect(() => {
        if (!apiFilter?.marketplace_id || apiFilter?.sku || apiFilter?.group_by) return;
        if (activeFilters.length > 0) return;
        maybeFetchTotals(apiFilter);
    }, [apiFilter?.marketplace_id, apiFilter?.start_date, apiFilter?.end_date, apiFilter?.sku, apiFilter?.group_by, activeFilters.length, maybeFetchTotals]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        setActiveFilters([]);
        setSortState({ columnKey: null, order: null });
        setPage(1);
        setExpandedRowKeys([]);
        lastTotalsKeyRef.current = null;
        setApiFilter({
            marketplace_id: selectedMarketplace?.id,
            start_date:     selectedTileData?.start_date,
            end_date:       selectedTileData?.end_date,
            sku:            selectedTileData?.sku,
            ...(groupBy ? { group_by: groupBy } : {}),
        });
    }, [
        selectedMarketplace?.id,
        selectedTileData?.start_date,
        selectedTileData?.end_date,
        selectedTileData?.sku,
        groupBy,
    ]);

    // ─── Sort current response page ──────────────────────────────────────────
    const sortedProducts = useMemo(() => {
        const { columnKey, order } = sortState;
        if (!columnKey || !order) return filteredProducts;
        const fn = COLUMN_SORTERS[columnKey];
        if (!fn) return filteredProducts;
        const sorted = [...filteredProducts].sort(fn);
        return order === 'descend' ? sorted.reverse() : sorted;
    }, [filteredProducts, sortState]);

    const pagedProducts = sortedProducts;

    // ─── Column definitions ───────────────────────────────────────────────────
    const allColumns = useMemo(() => [
        {
            title: '#',
            key: 'index',
            width: Math.max(70, plHeaderColumnWidth('#')),
            align: 'center', // Centers the number
            fixed: 'left',
            render: (_, record, index) =>
                record._isGroupChild
                    ? <span style={{ fontSize: 18, color: '#adb5bd', lineHeight: 1 }}></span>
                    : (page - 1) * pageSize + index + 1,
        },
        {
            title: 'Product Details',
            key: 'productDetails',
            fixed: 'left',
            width: Math.max(450, plHeaderColumnWidth('Product Details')),
            render: (_, record) => {
                const productUrl = record?.asin ? `${AMAZON_BASE_URL}${record.asin}` : '#';
                const hasChildren = Array.isArray(record.children) && record.children.length > 1;
                const variantCount = hasChildren ? record.children.length : 0;

                // ── Tag group parent: no image, just styled tag label + variant count ──
                if (record._isGroupParent && record._groupType === 'tag') {
                    const tagLabel = record._groupKey || record?.product_title || '—';
                    const displayLabel = tagLabel === '__untagged__' ? 'No Tag' : tagLabel;
                    const tc = getTagColor(displayLabel);
                    return (
                        <div className="d-flex align-items-center bdr-left-tbl bg-gray-200 rounded-4 p-3 py-2"
                             style={{ borderLeft: `3px solid ${tc.accent}` }}>
                            <div className="d-flex align-items-center gap-2">
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    width: 38, height: 38, borderRadius: '50%',
                                    backgroundColor: tc.bg, flexShrink: 0,
                                }}>
                                    <i className="bi bi-tag-fill" style={{ fontSize: 18, color: tc.accent }} />
                                </span>
                                <div className="d-flex flex-column">
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="fw-bold fs-6 text-dark">
                                            {displayLabel}
                                        </span>
                                        {variantCount > 0 && (
                                            <span style={{
                                                fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap',
                                                padding: '2px 7px', borderRadius: 4,
                                                border: '1px solid #adb5bd', color: '#555',
                                                lineHeight: 1.6,
                                            }}>
                                                {variantCount} variants
                                            </span>
                                        )}
                                    </div>
                                    <span className="text-muted fs-8 mt-1">Tag group</span>
                                </div>
                            </div>
                        </div>
                    );
                }

                return (
                    <div className="d-flex align-items-center bdr-left-tbl bg-gray-200 rounded-4 p-3 py-2"
                         style={record._isGroupParent ? { borderLeft: '3px solid #314F71' } : {}}>
                        <a href={productUrl} target="_blank" rel="noopener noreferrer"
                           className="symbol symbol-65px overflow-hidden me-3 border border-gray-300 rounded-2 shadow-sm">
                            <img src={record?.image_url || fakeProImage} alt="product" style={{ objectFit: 'contain' }} />
                        </a>
                        <div className="d-flex flex-column">
                            {/* ── Title + inline variant count for Parent ASIN group ── */}
                            <div className="d-flex align-items-center gap-2 mb-1">
                                <a className="text-gray-800 fs-6 text-hover-danger fw-bolder text-one min-w-250px"
                                   href={productUrl} target="_blank" rel="noopener noreferrer">
                                    {record?.product_title || ''}
                                </a>
                                {record._isGroupParent && variantCount > 0 && (
                                    <span style={{
                                        fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0,
                                        padding: '2px 7px', borderRadius: 4,
                                        border: '1px solid #adb5bd', color: '#555',
                                        lineHeight: 1.6,
                                    }}>
                                        {variantCount} variants
                                    </span>
                                )}
                            </div>

                            {/* ── Variant badge (child rows) ───────────────── */}
                            {record._isGroupChild && (
                                <div className="d-flex align-items-center flex-wrap gap-1 mb-1">
                                    <span style={{
                                        fontSize: 10, fontWeight: 500,
                                        padding: '2px 7px', borderRadius: 4,
                                        border: '1px dashed #adb5bd', color: '#888',
                                        lineHeight: 1.6,
                                    }}>
                                        <i className="bi bi-arrow-return-right me-1" style={{ fontSize: 9 }} />
                                        Variant
                                    </span>
                                </div>
                            )}

                            {/* ── SKU / ASIN / price lines ─────────────────── */}
                            <div className="fs-7 mt-1">
                                {!(record._isGroupParent && hasChildren) && (
                                    <><span><b>SKU:</b> {record?.sku}</span>{' / '}</>
                                )}
                                <span><b>ASIN:</b> {record?.asin}</span>
                            </div>
                            {(!record._isGroupParent || !record.children) && (
                                <div className="fs-7 mt-1 d-flex align-items-center flex-wrap gap-1">
                                    {record?.listing_status && (() => {
                                        const status = record.listing_status;
                                        const lower  = status.toLowerCase();
                                        let bg, color, border;
                                        if (lower === 'active') {
                                            bg = '#e6f4ea'; color = '#1a7f37'; border = '#b7dfbf';
                                        } else if (lower.includes('out of stock')) {
                                            bg = '#fff1f0'; color = '#cf1322'; border = '#ffa39e';
                                        } else if (lower.includes('Search Suppressed')) {
                                            bg = '#fff7e6'; color = '#d46b08'; border = '#ffd591';
                                        } else {
                                            bg = '#f0f0f0'; color = '#595959'; border = '#d9d9d9';
                                        }
                                        return (
                                            <span style={{
                                                fontSize: 10, fontWeight: 600, lineHeight: 1,
                                                padding: '3px 8px', borderRadius: 20,
                                                background: bg, color, border: `1px solid ${border}`,
                                                whiteSpace: 'nowrap', letterSpacing: '0.02em',
                                            }}>
                                                {status}
                                            </span>
                                        );
                                    })()}
                                    <span><b>{getAmount(record?.price, selectedCurrencySign)}</b></span>
                                    {' / '}<span>{record?.fulfillment_channel}</span>
                                    {' / '}
                                    <span
                                        className="d-inline-flex align-items-center gap-1"
                                        style={{ cursor: 'pointer' }}
                                        onClick={(e) => handleEditCogs(e, record)}
                                        title="Edit COGS"
                                    >
                                        COGS {getAmount(record?.item_cogs, selectedCurrencySign)}
                                        <i className="ki-outline ki-pencil" style={{ color: '#f45b68', fontSize: '11px', marginLeft: '3px' }} />
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                );
            },
        },
        {
            title: productColTitle('Tags', 'tags'),
            label: 'Tags',
            key: 'tags',
            dataIndex: 'tags',
            width: plHeaderColumnWidth('Tags', { hasInfoIcon: true }),
            render: (tags) => {
                if (!tags?.length) return <span className="text-muted fs-8">—</span>;
                return (
                    <div className="d-flex flex-wrap gap-1">
                        {tags.map((tag, idx) => {
                            const tc = getTagColor(tag);
                            return (
                                <Tag key={idx} style={{ color: tc.text, borderColor: tc.border, background: tc.bg, fontSize: '11px' }}>
                                    {tag}
                                </Tag>
                            );
                        })}
                    </div>
                );
            },
        },
        { 
            title: productColTitle('Units Sold', 'total_units'),
            label: 'Units Sold',
            key: 'total_units',   
            dataIndex: 'total_units',
            width: plHeaderColumnWidth('Units Sold', { hasInfoIcon: true, hasSorter: true }),
            sorter: COLUMN_SORTERS.total_units,      
            sortOrder: sortState.columnKey === 'total_units' ? sortState.order : null,
            render: (val) => AddCommas(val)
        },
   
        {
            title: productColTitle('Refund Units', 'refund_unit'),
            label: 'Refund Units',
            key: 'refund_unit', dataIndex: 'refund_unit',
            width: plHeaderColumnWidth('Refund Units', { hasInfoIcon: true, hasSorter: true }),
            sorter: COLUMN_SORTERS.refund_unit, sortOrder: sortState.columnKey === 'refund_unit' ? sortState.order : null,
            render: (val, record) => {
                const reasons = record?.refund_breakup?.reasons || [];
                return (
                    <Tooltip placement="top" color="#1e1e2d" title={
                        <div className="p-2" style={{ minWidth: '200px' }}>
                            {reasons.length > 0
                                ? reasons.map((item, idx) => (
                                    <div key={idx} className="d-flex justify-content-between gap-3 mb-1">
                                        <span className="text-capitalize">{item.reason.toLowerCase().replace(/_/g, ' ')}:</span>
                                        <b>{AddCommas(item.count)}</b>
                                    </div>
                                ))
                                : <div className="text-muted">No specific reasons provided</div>}
                        </div>
                    }>
                        <span className="fw-bold cursor-pointer text-danger" style={{ borderBottom: '1px dotted #ff4d4f' }}>{AddCommas(val)}</span>
                    </Tooltip>
                );
            },
        },
   
        {
            title: productColTitle('Refund Cost', 'refund_cost'),
            label: 'Refund Cost',
            key: 'refund_cost', dataIndex: 'refund_cost',
            width: plHeaderColumnWidth('Refund Cost', { hasInfoIcon: true, hasSorter: true }),
            sorter: COLUMN_SORTERS.refund_cost, sortOrder: sortState.columnKey === 'refund_cost' ? sortState.order : null,
            render: (val, record) => {
                const breakdown = record?.more_data?.refund_cost || {};
                return (
                    <Tooltip placement="top" color="#1e1e2d" title={
                        <div className="p-2" style={{ minWidth: '220px' }}>
                            <div className="d-flex justify-content-between gap-3 mb-1"><span>Total:</span><b>{getAmount(breakdown.total ?? 0, selectedCurrencySign)}</b></div>
                            <div className="d-flex justify-content-between gap-3 mb-1"><span>Refunded Amount:</span><b>{getAmount(breakdown.refunded_amount ?? 0, selectedCurrencySign)}</b></div>
                            <div className="d-flex justify-content-between gap-3 mb-1"><span>Fixed Closing Fee:</span><b>{getAmount(breakdown.fixed_closing_fee ?? 0, selectedCurrencySign)}</b></div>
                            <div className="d-flex justify-content-between gap-3"><span>Ship Promotion:</span><b>{getAmount(breakdown.ship_promotion ?? 0, selectedCurrencySign)}</b></div>
                        </div>
                    }>
                        <span className="fw-bold cursor-pointer text-danger" style={{ borderBottom: '1px dotted #ff4d4f' }}>{getAmount(val, selectedCurrencySign)}</span>
                    </Tooltip>
                );
            },
        },
        { title: productColTitle('Sales', 'sales'), label: 'Sales',         key: 'sales',         dataIndex: 'sales',         width: plHeaderColumnWidth('Sales', { hasInfoIcon: true, hasSorter: true }), sorter: COLUMN_SORTERS.sales,            sortOrder: sortState.columnKey === 'sales'         ? sortState.order : null, render: (val) => <b className="text-gray-800">{getAmount(val, selectedCurrencySign)}</b> },
        { title: productColTitle('Ad Sales', 'ad_sales'), label: 'Ad Sales',      key: 'ad_sales',      dataIndex: 'ad_sales',      width: plHeaderColumnWidth('Ad Sales', { hasInfoIcon: true, hasSorter: true }), sorter: COLUMN_SORTERS.ad_sales,         sortOrder: sortState.columnKey === 'ad_sales'      ? sortState.order : null, render: (val) => getAmount(val, selectedCurrencySign) },
        { title: productColTitle('Ad Spend', 'ad_spend'), label: 'Ad Spend',      key: 'ad_spend',      dataIndex: 'ad_spend',      width: plHeaderColumnWidth('Ad Spend', { hasInfoIcon: true, hasSorter: true }), sorter: COLUMN_SORTERS.ad_spend,         sortOrder: sortState.columnKey === 'ad_spend'      ? sortState.order : null, render: (val) => getAmount(val, selectedCurrencySign) },
        { title: productColTitle('COGS', 'cogs'), label: 'COGS',          key: 'cogs',          dataIndex: 'cogs',          width: plHeaderColumnWidth('COGS', { hasInfoIcon: true, hasSorter: true }), sorter: COLUMN_SORTERS.cogs,             sortOrder: sortState.columnKey === 'cogs'          ? sortState.order : null, render: (val) => getAmount(val, selectedCurrencySign) },
        { title: productColTitle('Organic Sales', 'organic_sales'), label: 'Organic Sales', key: 'organic_sales', dataIndex: 'organic_sales', width: plHeaderColumnWidth('Organic Sales', { hasInfoIcon: true, hasSorter: true }), sorter: COLUMN_SORTERS.organic_sales,    sortOrder: sortState.columnKey === 'organic_sales' ? sortState.order : null, render: (val) => getAmount(val, selectedCurrencySign) },
        {
            title: productColTitle('Tax Collected', 'tax'),
            label: 'Tax Collected',
            key: 'tax', dataIndex: ['more_data', 'tax_liability', 'total'],
            width: plHeaderColumnWidth('Tax Collected', { hasInfoIcon: true, hasSorter: true }),
            sorter: COLUMN_SORTERS.tax, sortOrder: sortState.columnKey === 'tax' ? sortState.order : null,
            render: (val) => getAmount(val || 0, selectedCurrencySign),
        },
        { title: productColTitle('Fees', 'fees'), label: 'Fees',         key: 'fees',         dataIndex: 'fees',         width: plHeaderColumnWidth('Fees', { hasInfoIcon: true, hasSorter: true }), sorter: COLUMN_SORTERS.fees,             sortOrder: sortState.columnKey === 'fees'          ? sortState.order : null, render: (val) => getAmount(val, selectedCurrencySign) },
        { title: productColTitle('Gross Profit', 'gross_profit'), label: 'Gross Profit', key: 'gross_profit', dataIndex: 'gross_profit', width: plHeaderColumnWidth('Gross Profit', { hasInfoIcon: true, hasSorter: true }), sorter: COLUMN_SORTERS.gross_profit,      sortOrder: sortState.columnKey === 'gross_profit'  ? sortState.order : null, render: (val) => getAmount(val, selectedCurrencySign) },
        { title: productColTitle('Net Profit', 'net_profit'), label: 'Net Profit',   key: 'net_profit',   dataIndex: 'net_profit',   width: plHeaderColumnWidth('Net Profit', { hasInfoIcon: true, hasSorter: true }), sorter: COLUMN_SORTERS.net_profit,       sortOrder: sortState.columnKey === 'net_profit'    ? sortState.order : null, render: (val) => <span className="fw-bold text-success">{getAmount(val, selectedCurrencySign)}</span> },
        {
            title: productColTitle('FBA Inventory', 'inventory'),
            label: 'FBA Inventory',
            key: 'inventory',
            dataIndex: 'inventory',
            width: plHeaderColumnWidth('FBA Inventory', { hasInfoIcon: true, hasSorter: true }),
            sorter: COLUMN_SORTERS.inventory,
            sortOrder: sortState.columnKey === 'inventory' ? sortState.order : null,
            render: (val, record) => {
                const inv = record?.more_data?.total_inventories || {};
                const ledgerWarehouse = record?.ledger_warehouse_inventory; // ✅ FIXED
        
                return (
                    <Tooltip
                        placement="top"
                        color="#1e1e2d"
                        title={
                            <div className="p-2" style={{ minWidth: '180px' }}>
                                <div>FBA Available: <b>{inv.fba_available_inventory ?? 0}</b></div>
                                {ledgerWarehouse?.by_location?.length > 0 && (
                                    <div style={{ marginTop: 2, marginLeft:5 }}>
                                        {/* <div className="fw-bold mb-1" style={{ fontSize: '12px' }}>
                                            Warehouse Breakdown:
                                        </div> */}
                                {/* ✅ Corrected warehouse breakdown */}

                                        {ledgerWarehouse.by_location.map((loc, idx) => (
                                            <div
                                                key={loc.location || idx}
                                                className="d-flex align-items-center"
                                                style={{ fontSize: '12px' }}
                                            >
                                                <i className='ki-outline ki-arrow-right fs-7'></i>
                                                {loc.fc_details ? (
                                                    <span>{loc.location} ({loc.fc_details.macro_region}):</span>
                                                ) : (
                                                    <span>{loc.location}:</span>
                                                )}
                                           
                                                <span className="ms-2">{loc.sellable_ending_balance}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div>Reserved: <b>{inv.reserved_inventory ?? 0}</b></div>
                                <div>Inbound: <b>{inv.inbound_inventory ?? 0}</b></div>
        
                                
                            </div>
                        }
                    >
                        <span className="fw-bold cursor-pointer" style={{ borderBottom: '1px dotted #adadad' }}>
                            {val}
                        </span>
                    </Tooltip>
                );
            },
        },
        { title: productColTitle('FBM Inventory', 'fbm_inventory'), label: 'FBM Inventory', key: 'fbm_inventory', dataIndex: 'fbm_inventory', width: plHeaderColumnWidth('FBM Inventory', { hasInfoIcon: true, hasSorter: true }), sorter: COLUMN_SORTERS.fbm_inventory, sortOrder: sortState.columnKey === 'fbm_inventory' ? sortState.order : null },
        { title: productColTitle('Days Until Stockout', 'days_on_hand'), label: 'Days Until Stockout', key: 'days_on_hand', dataIndex: 'days_on_hand', width: plHeaderColumnWidth('Days Until Stockout', { hasInfoIcon: true, hasSorter: true }), sorter: COLUMN_SORTERS.days_on_hand, sortOrder: sortState.columnKey === 'days_on_hand' ? sortState.order : null },
        {
            title: productColTitle('FBA Inventory Health', 'inventory_status'),
            label: 'FBA Inventory Health',
            key: 'inventory_status', dataIndex: 'inventory_status',
            width: plHeaderColumnWidth('FBA Inventory Health', { hasInfoIcon: true, hasSorter: true }),
            sorter: COLUMN_SORTERS.inventory_status, sortOrder: sortState.columnKey === 'inventory_status' ? sortState.order : null,
            render: (status) => {
                let badgeClass = 'badge-light-primary';
                const lower = status?.toLowerCase() || '';
                if (lower.includes('in stock') || lower === 'healthy')         badgeClass = 'badge-light-success';
                else if (lower.includes('low') || lower.includes('alert') || lower.includes('at risk'))     badgeClass = 'badge-light-warning';
                else if (lower.includes('out') || lower.includes('overstock')) badgeClass = 'badge-light-danger';
                return <span className={`badge ${badgeClass} fw-bold px-4 py-3`}>{status || 'N/A'}</span>;
            },
        },
        // ─── Additional columns (hidden by default) ───────────────────────
        { title: productColTitle('CPC', 'cpc'), label: 'CPC',   key: 'cpc',   dataIndex: ['more_data', 'cpc'],   width: plHeaderColumnWidth('CPC', { hasInfoIcon: true, hasSorter: true }), sorter: COLUMN_SORTERS.cpc,   sortOrder: sortState.columnKey === 'cpc'   ? sortState.order : null, render: (val) => getAmount(val, selectedCurrencySign) },
        { title: productColTitle('ACoS', 'acos'), label: 'ACoS',  key: 'acos',  dataIndex: ['more_data', 'acos'],  width: plHeaderColumnWidth('ACoS', { hasInfoIcon: true, hasSorter: true }), sorter: COLUMN_SORTERS.acos,  sortOrder: sortState.columnKey === 'acos'  ? sortState.order : null, render: (val) => `${val ?? 0}%` },
        { title: productColTitle('RoAS', 'roas'), label: 'RoAS',  key: 'roas',  dataIndex: 'roas',                  width: plHeaderColumnWidth('RoAS', { hasInfoIcon: true, hasSorter: true }), sorter: COLUMN_SORTERS.roas,  sortOrder: sortState.columnKey === 'roas'  ? sortState.order : null },
        { title: productColTitle('TRoAS', 'troas'), label: 'TRoAS', key: 'troas', dataIndex: ['more_data', 'TRoAS'], width: plHeaderColumnWidth('TRoAS', { hasInfoIcon: true, hasSorter: true }), sorter: COLUMN_SORTERS.troas, sortOrder: sortState.columnKey === 'troas' ? sortState.order : null },
        { title: productColTitle('TACoS', 'tacos'), label: 'TACoS', key: 'tacos', dataIndex: ['more_data', 'TACoS'], width: plHeaderColumnWidth('TACoS', { hasInfoIcon: true, hasSorter: true }), sorter: COLUMN_SORTERS.tacos, sortOrder: sortState.columnKey === 'tacos' ? sortState.order : null, render: (val) => `${val ?? 0}%` },
        // { title: 'BSR',   key: 'sales_rank',   dataIndex: 'sales_rank',   width: 100, sorter: COLUMN_SORTERS.sales_rank,   sortOrder: sortState.columnKey === 'sales_rank'   ? sortState.order : null },
        {
            title: productColTitle('BSR', 'sales_rank'),
            label: 'BSR',
            key: 'sales_rank',
            dataIndex: 'sales_rank',
            width: plHeaderColumnWidth('BSR', { hasInfoIcon: true, hasSorter: true }),
            sorter: COLUMN_SORTERS.sales_rank,
            sortOrder: sortState.columnKey === 'sales_rank' ? sortState.order : null,
            render: (val, record) => {
                if (!val && val !== 0) return '—';
                return (
                    <span
                        className="fw-bold cursor-pointer"
                        style={{
                            color: '#314F71',
                            borderBottom: '1px dotted #314F71',
                            whiteSpace: 'nowrap',
                        }}
                        onClick={(e) => handleOpenBsr(e, record)}
                        title="View BSR trend"
                    >
                        #{Number(val).toLocaleString()}
                    </span>
                );
            },
        },
        { title: productColTitle('Ad Attribution', 'ad_attribution'), label: 'Ad Attribution',     key: 'ad_attribution',     dataIndex: ['more_data', 'ad_attribution'],       width: plHeaderColumnWidth('Ad Attribution', { hasInfoIcon: true, hasSorter: true }), sorter: COLUMN_SORTERS.ad_attribution,     sortOrder: sortState.columnKey === 'ad_attribution'     ? sortState.order : null, render: (val) => `${val ?? 0}%` },
        { title: productColTitle('Profit %', 'profit_percent'), label: 'Profit %',          key: 'profit_percent',     dataIndex: ['more_data', 'profit_percent'],       width: plHeaderColumnWidth('Profit %', { hasInfoIcon: true, hasSorter: true }), sorter: COLUMN_SORTERS.profit_percent,     sortOrder: sortState.columnKey === 'profit_percent'     ? sortState.order : null, render: (val) => `${val ?? 0}%` },
        { title: productColTitle('Sales Contribution', 'sales_contribution'), label: 'Sales Contribution', key: 'sales_contribution', dataIndex: ['more_data', 'asin_sale_contribution'], width: plHeaderColumnWidth('Sales Contribution', { hasInfoIcon: true, hasSorter: true }), sorter: COLUMN_SORTERS.sales_contribution, sortOrder: sortState.columnKey === 'sales_contribution' ? sortState.order : null, render: (val) => `${val ?? 0}%` },
        {
            title: 'Details', key: 'details', width: 110,
            render: (_, record) => (
                <span className="cursor-pointer fw-bold  text-dark text-hover-danger" 
                      onClick={() => openDetails(record)}>
                    See Details
                </span>
            ),
        },
    ], [selectedCurrencySign, page, pageSize, openDetails, sortState]);

    // ─── Column visibility ────────────────────────────────────────────────────
    const HIDDEN_BY_DEFAULT = useMemo(() => new Set([
        'tags', 'cpc', 'acos', 'roas', 'troas', 'tacos', 'sales_rank',
        'ad_attribution', 'profit_percent', 'sales_contribution',
    ]), []);

    const [visibleColumns, setVisibleColumns] = useState(
        () => allColumns.map(col => col.key).filter(key => !HIDDEN_BY_DEFAULT.has(key))
    );

    const columns = useMemo(
        () => allColumns.filter(col => visibleColumns.includes(col.key)),
        [allColumns, visibleColumns]
    );

    const toggleableColumns = useMemo(
        () => allColumns.filter(col => col.key !== 'index' && col.key !== 'productDetails' && col.key !== 'details'),
        [allColumns]
    );

    const handleColumnToggle = useCallback(checkedValues => {
        setVisibleColumns(['index', 'productDetails', 'details', ...checkedValues]);
    }, []);

    useEffect(() => {
        if (!isMobile && onColumnsReady) {
            onColumnsReady({ toggleableColumns, visibleColumns, handleColumnToggle });
        }
    }, [toggleableColumns, visibleColumns, handleColumnToggle, isMobile, onColumnsReady]);

    useEffect(() => {
        if (isMobile && onColumnsReady) onColumnsReady(null);
    }, [isMobile, onColumnsReady]);


    const handleOpenBsr = useCallback((e, record) => {
        e.stopPropagation();
        setSelectedBsrRecord(record);
        setIsBsrModalOpen(true);
    }, []);

    // ─── Mobile cards ─────────────────────────────────────────────────────────
    const renderMobileCards = () => (
        <div className="d-flex flex-column gap-3">
            {pagedProducts.map((item, index) => (
                <div key={item.sku || index} className="card border-0 shadow-sm p-4 rounded-4 cursor-pointer"
                     style={{ background: '#fff', border: '1px solid #f0f0f0' }} onClick={() => openDetails(item)}>
                    <div className="d-flex align-items-center mb-4">
                        <img src={item.image_url || fakeProImage} alt="pro" className="rounded-2 me-3"
                             style={{ width: '50px', height: '50px', objectFit: 'contain', border: '1px solid #f5f5f5' }} />
                        <div className="overflow-hidden">
                            <div className="fw-bolder text-gray-800 text-truncate fs-6" style={{ maxWidth: '220px' }}>{item.product_title || 'N/A'}</div>
                            <div className="text-muted fs-8"><b>SKU:</b> {item.sku} <span className="mx-1">|</span> <b>ASIN:</b> {item.asin}</div>
                        </div>
                    </div>
                    <div className="row g-4 border-top pt-4">
                        <div className="col-4"><div className="fs-9 text-uppercase text-muted fw-bold mb-1">Units</div><div className="fw-bolder text-dark">{item.total_units}</div></div>
                        <div className="col-4"><div className="fs-9 text-uppercase text-muted fw-bold mb-1">Sales</div><div className="fw-bolder text-dark">{getAmount(item.sales, selectedCurrencySign)}</div></div>
                        <div className="col-4"><div className="fs-9 text-uppercase text-muted fw-bold mb-1">Net Profit</div><div className={`fw-bolder ${item.net_profit >= 0 ? 'text-success' : 'text-danger'}`}>{getAmount(item.net_profit, selectedCurrencySign)}</div></div>
                        <div className="col-4">
                            <div className="fs-9 text-uppercase text-muted fw-bold mb-1">Refunds</div>
                            <Tooltip trigger="click" color="#1e1e2d" title={
                                <div className="p-2" style={{ minWidth: '180px' }}>
                                    <div className="fw-bold mb-2 border-bottom pb-1" style={{ color: '#ff4d4f' }}>Refund Reasons</div>
                                    {(item?.refund_breakup?.reasons || []).length > 0
                                        ? item.refund_breakup.reasons.map((r, idx) => (
                                            <div key={idx} className="d-flex justify-content-between gap-3 mb-1 fs-8">
                                                <span className="text-capitalize">{r.reason.toLowerCase().replace(/_/g, ' ')}:</span>
                                                <b>{r.count}</b>
                                            </div>
                                        ))
                                        : <div className="fs-8 text-muted">No reasons provided</div>}
                                </div>
                            }>
                                <div className="fw-bolder text-danger" style={{ borderBottom: '1px dotted #ff4d4f', display: 'inline-block' }}>{item.refund_unit}</div>
                            </Tooltip>
                        </div>
                        <div className="col-4"><div className="fs-9 text-uppercase text-muted fw-bold mb-1">Ref. Cost</div><div className="fw-bolder text-muted">{getAmount(item.refund_cost, selectedCurrencySign)}</div></div>
                        <div className="col-4"><div className="fs-9 text-uppercase text-muted fw-bold mb-1">Ad Spend</div><div className="fw-bolder text-primary">{getAmount(item.ad_spend, selectedCurrencySign)}</div></div>
                    </div>
                </div>
            ))}
        </div>
    );

    // ─── Summary row ──────────────────────────────────────────────────────────

    // bannerHeight is needed both for the sticky offset and the intersection observer
    const bannerHeight = userData?.user?.user_data?.trial_expires_at ? 40 : 0;

    // Track whether the table header is currently in "sticky" state.
    // We do this by observing a 1-px sentinel placed at the very top of the
    // table container: when it leaves the viewport (adjusted for bannerHeight)
    // the header has snapped into sticky mode, so we reveal the sticky footer.
    useEffect(() => {
        const container = tableContainerRef.current;
        if (!container || isMobile) return;

        const sentinel = document.createElement('div');
        sentinel.style.cssText = 'position:absolute;top:0;height:1px;width:100%;pointer-events:none;';
        container.style.position = 'relative';
        container.prepend(sentinel);

        const observer = new IntersectionObserver(
            ([entry]) => setIsHeaderSticky(!entry.isIntersecting),
            { threshold: 0, rootMargin: `-${bannerHeight + 1}px 0px 0px 0px` }
        );
        observer.observe(sentinel);

        return () => {
            observer.disconnect();
            sentinel.remove();
        };
    }, [bannerHeight, isMobile]);

    // ─── Force-sync Total-row horizontal alignment when it first mounts ─────────
    // Problem: renderSummary returns null while !isHeaderSticky, so Table.Summary
    // is completely unmounted during horizontal scrolling. When isHeaderSticky flips
    // to true the summary mounts fresh with scrollLeft = 0, causing misalignment.
    // Ant Design only re-syncs the summary table position when a scroll event fires
    // on its internal scroll container — so we dispatch one immediately after the
    // summary has been inserted into the DOM (double-RAF ensures React has flushed
    // the render and Ant Design's listener is attached before the event fires).
    useEffect(() => {
        if (!isHeaderSticky) return;
        let raf2;
        const raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(() => {
                const container = tableContainerRef.current;
                if (!container) return;
                // x-only scroll tables use .ant-table-content; y+x tables use .ant-table-body
                const scrollEl =
                    container.querySelector('.ant-table-body') ||
                    container.querySelector('.ant-table-content');
                if (scrollEl) {
                    scrollEl.dispatchEvent(new Event('scroll', { bubbles: true }));
                }
            });
        });
        return () => {
            cancelAnimationFrame(raf1);
            if (raf2) cancelAnimationFrame(raf2);
        };
    }, [isHeaderSticky]);

    const renderSummary = () => {
        if (groupBy) return null;                   // hide totals when grouped
        if (!isHeaderSticky) return null;           // hide footer until header is sticky
        if (!totals && !totalsLoading) return null;

        const cell = (value) =>
            totalsLoading ? (
                <span className="placeholder-glow d-inline-block" style={{ minWidth: '60px' }}>
                    <span className="placeholder col-10 rounded" style={{ height: '14px', display: 'block' }} />
                </span>
            ) : value;

        const summaryDataMap = {
            total_units:       cell(totals?.total_units),
            refund_unit:       cell(totals?.refund_unit),
            refund_cost:       cell(getAmount(totals?.refund_cost, selectedCurrencySign)),
            sales:             cell(<span className="text-dark">{getAmount(totals?.sales, selectedCurrencySign)}</span>),
            ad_sales:          cell(getAmount(totals?.ad_sales, selectedCurrencySign)),
            ad_spend:          cell(getAmount(totals?.ad_spend, selectedCurrencySign)),
            cogs:              cell(totals?.cogs?.toFixed(2)),
            organic_sales:     cell(getAmount(totals?.organic_sales, selectedCurrencySign)),
            tax:               cell(getAmount(totals?.tax, selectedCurrencySign)),
            fees:              cell(getAmount(totals?.fees, selectedCurrencySign)),
            gross_profit:      cell(getAmount(totals?.gross_profit, selectedCurrencySign)),
            net_profit:        cell(
                <span className={totals?.net_profit >= 0 ? 'text-success' : 'text-danger'}>
                    {getAmount(totals?.net_profit, selectedCurrencySign)}
                </span>
            ),
            inventory:         cell(totals?.inventory),
            fbm_inventory:     cell(totals?.fbm_inventory),
            cpc:               cell(getAmount(totals?.cpc, selectedCurrencySign)),
            acos:              cell(`${totals?.acos ?? 0}%`),
            roas:              cell(totals?.roas),
            troas:             cell(totals?.TRoAS),
            tacos:             cell(`${totals?.TACoS ?? 0}%`),
            ad_attribution:    cell(`${totals?.ad_attribution ?? 0}%`),
            profit_percent:    cell(`${totals?.profit_percent ?? 0}%`),
            sales_contribution: cell(`${totals?.sales_contribution ?? 0}%`),
        };

        return (
            <Table.Summary fixed>
                <Table.Summary.Row className="bg-light fw-bolder products-pl-summary-row">
                    {columns.map((col, index) => (
                        <Table.Summary.Cell key={col.key} index={index}>
                            {col.key === 'index'
                                ? ''
                                : col.key === 'productDetails'
                                    ? 'TOTALS'
                                    : (summaryDataMap[col.key] || '')}
                        </Table.Summary.Cell>
                    ))}
                </Table.Summary.Row>
            </Table.Summary>
        );
    };


    return (
        <div className="products-container">
            <div className="row gy-5 g-xl-5">
                <div className="col-xxl-12">
                    {isMobile ? (
                        <div className="mobile-view-container px-1">
                            {pagedProducts.length > 0 ? renderMobileCards() : <Empty description="No Products Found" />}
                        </div>
                    ) : (
                        <div ref={tableContainerRef} className="shadow-sm rounded">
                            <Table
                                scroll={{ x: 'max-content' }}
                                sticky={{ offsetHeader: bannerHeight }}
                                columns={columns}
                                loading={tableLoading}
                                dataSource={pagedProducts}
                                rowKey={record => record._rowKey || record.sku || record.asin}
                                pagination={false}
                                summary={renderSummary}
                                locale={{ emptyText: <Empty description="No Products Found" /> }}
                                components={{
                                    header: {
                                        row: props => <tr {...props} className={`${props.className || ''} fw-bolder text-dark bg-cus`} />,
                                        cell: ProductTableHeaderCell,
                                    },
                                }}
                                onChange={handleTableChange}
                                showSorterTooltip={false}
                                indentSize={20}
                                onRow={record => ({
                                    style: record._isGroupParent
                                        ? { fontWeight: 600 }
                                        : record._isGroupChild
                                            ? {
                                                background: '#f4f6f9',
                                                fontSize: '0.92em',
                                                color: '#555',
                                                borderLeft: '3px solid #dce3ec',
                                              }
                                            : {},
                                })}
                                rowClassName={record => {
                                    if (record._isGroupParent) return 'grouped-parent-row';
                                    if (record._isGroupChild)  return 'grouped-child-row';
                                    return '';
                                }}
                                expandable={groupBy ? {
                                    expandedRowKeys,
                                    onExpand: (expanded, record) => {
                                        setExpandedRowKeys(prev =>
                                            expanded
                                                ? [...prev, record._rowKey]
                                                : prev.filter(k => k !== record._rowKey)
                                        );
                                    },
                                    // Only rows with MORE than 1 variant should be expandable.
                                    rowExpandable: record =>
                                        record._isGroupParent &&
                                        Array.isArray(record.children) &&
                                        record.children.length > 1,
                                    expandIcon: ({ expanded, onExpand, record, expandable: rowExpandable }) => {
                                        if (!rowExpandable) return <span style={{ display: 'inline-block', width: 18 }} />;
                                        return (
                                            <span
                                                onClick={e => onExpand(record, e)}
                                                className="cursor-pointer me-1"
                                                style={{ color: '#314F71', fontSize: 12, lineHeight: 1, display: 'inline-block', width: 18 }}
                                            >
                                                {expanded
                                                    ? <i className="bi bi-chevron-down fw-bold-4" style={{color: '#212529', fontSize: 'large'}}/>
                                                    : <i className="bi bi-chevron-right fw-bold-4" style={{color: '#212529', fontSize: 'large'}}/>
                                                }
                                            </span>
                                        );
                                    },
                                } : undefined}
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className="card border-0 shadow-sm mt-5">
                <div className="card-body py-4">
                    <Pagination
                        current={page}
                        pageSize={pageSize}
                        total={totalCount || filteredProducts.length}
                        showSizeChanger
                        onChange={(newPage, newPageSize) => { setPage(newPage); setPageSize(newPageSize); }}
                        showTotal={(total, range) => {
                            const unit = groupBy ? 'groups' : 'products';
                            return isMobile
                                ? `Total: ${total} ${unit}`
                                : `${range[0]}-${range[1]} of ${total} ${unit}`;
                        }}
                        responsive
                        size={isMobile ? 'small' : 'default'}
                        simple={isMobile}
                    />
                </div>
            </div>

            <ProductDetailsModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                data={selectedRow}
                currencySign={selectedCurrencySign}
            />

            <AddCogs
                visible={isCogsModalOpen}
                onClose={() => { setIsCogsModalOpen(false); setSelectedCogsRecord(null); }}
                product={selectedCogsRecord}
                userData={userData}
                sku={selectedCogsRecord?.sku}
                marketplaceId={selectedMarketplace?.id}
                getList={refreshProducts}
                isUpdate={!!(selectedCogsRecord?.item_cogs)}
            />

            <SalesRankModal
                open={isBsrModalOpen}
                onClose={() => { setIsBsrModalOpen(false); setSelectedBsrRecord(null); }}
                asin={selectedBsrRecord?.asin}
                productTitle={selectedBsrRecord?.product_title}
                startDate={apiFilter?.start_date}
                endDate={apiFilter?.end_date}
                token={userData?.token}
            />

            <style>{`
                .products-container .products-pl-summary-row > td {
                    white-space: nowrap;
                    vertical-align: middle;
                }
            `}</style>
        </div>
    );
};

export default React.memo(Products);